/**
 * Ensure a value is a valid number
 * @param {any} value - Value to check
 * @param {number} defaultValue - Default value if invalid (default: 0)
 * @returns {number} - Valid number
 */
export const ensureNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return (isNaN(num) || value === null || value === undefined) ? defaultValue : num;
};

/**
 * Convert baseball innings notation to decimal innings
 * In baseball: X.Y where Y represents outs (0, 1, or 2)
 * - 6.0 = 6 innings (6.0 decimal)
 * - 6.1 = 6 innings + 1 out (6.333... decimal)
 * - 6.2 = 6 innings + 2 outs (6.667 decimal)
 * @param {string|number} innings - Innings in baseball notation
 * @returns {number} - Decimal innings for calculations
 */
export const convertBaseballInningsToDecimal = (innings) => {
  if (innings === undefined || innings === null || innings === '') return 0;

  const inningsNum = Number(innings);
  if (isNaN(inningsNum)) return 0;

  const wholePart = Math.floor(inningsNum);
  const decimalPart = inningsNum - wholePart;

  // Convert baseball outs notation to decimal fractions
  // 0.1 = 1 out = 1/3 inning
  // 0.2 = 2 outs = 2/3 inning
  if (Math.abs(decimalPart - 0.1) < 0.01) {
    return wholePart + (1/3);
  } else if (Math.abs(decimalPart - 0.2) < 0.01) {
    return wholePart + (2/3);
  }

  // If it's already in decimal format or 0 outs, return as-is
  return inningsNum;
};

/**
 * Format a decimal as a batting statistic (e.g., .345)
 * @param {number} value - Decimal value to format
 * @returns {string} - Formatted batting statistic
 */
export const formatBattingStat = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '.000';
  // Ensure value is between 0 and 1
  const normalizedValue = value > 1 ? value / 1000 : value;
  return normalizedValue.toFixed(3).replace(/^0+/, '');
};

/**
 * Calculate batting average
 * @param {number} hits - Number of hits
 * @param {number} atBats - Number of at-bats
 * @returns {number} - Raw batting average (not formatted)
 */
export const calculateBattingAverage = (hits, atBats) => {
  hits = ensureNumber(hits);
  atBats = ensureNumber(atBats);
  return atBats > 0 ? hits / atBats : 0;
};

/**
 * Calculate on-base percentage
 * @param {number} hits - Number of hits
 * @param {number} walks - Number of walks
 * @param {number} hitByPitch - Number of times hit by pitch
 * @param {number} atBats - Number of at-bats
 * @param {number} sacrificeFlies - Number of sacrifice flies
 * @returns {number} - Raw OBP (not formatted)
 */
export const calculateOBP = (hits, walks, hitByPitch, atBats, sacrificeFlies = 0) => {
  hits = ensureNumber(hits);
  walks = ensureNumber(walks);
  hitByPitch = ensureNumber(hitByPitch);
  atBats = ensureNumber(atBats);
  sacrificeFlies = ensureNumber(sacrificeFlies);
  
  const numerator = hits + walks + hitByPitch;
  const denominator = atBats + walks + hitByPitch + sacrificeFlies;
  return denominator > 0 ? numerator / denominator : 0;
};

/**
 * Calculate total bases
 * @param {number} hits - Total hits
 * @param {number} doubles - Number of doubles
 * @param {number} triples - Number of triples
 * @param {number} homeRuns - Number of home runs
 * @returns {number} - Total bases
 */
export const calculateTotalBases = (hits, doubles, triples, homeRuns) => {
  hits = ensureNumber(hits);
  doubles = ensureNumber(doubles);
  triples = ensureNumber(triples);
  homeRuns = ensureNumber(homeRuns);
  
  // Calculate singles (ensure it's not negative)
  const singles = Math.max(0, hits - doubles - triples - homeRuns);
  
  return singles + (2 * doubles) + (3 * triples) + (4 * homeRuns);
};

/**
 * Calculate slugging percentage
 * @param {number} totalBases - Total bases
 * @param {number} atBats - Number of at-bats
 * @returns {number} - Raw slugging percentage (not formatted)
 */
export const calculateSLG = (totalBases, atBats) => {
  totalBases = ensureNumber(totalBases);
  atBats = ensureNumber(atBats);
  return atBats > 0 ? totalBases / atBats : 0;
};

/**
 * Calculate OPS (On-base Plus Slugging)
 * @param {number} obp - On-base percentage
 * @param {number} slg - Slugging percentage
 * @returns {number} - Raw OPS (not formatted)
 */
export const calculateOPS = (obp, slg) => {
  obp = ensureNumber(obp);
  slg = ensureNumber(slg);
  return obp + slg;
};

/**
 * Calculate singles from total hits and extra-base hits
 * @param {number} hits - Total hits
 * @param {number} doubles - Number of doubles
 * @param {number} triples - Number of triples
 * @param {number} homeRuns - Number of home runs
 * @returns {number} - Number of singles
 */
export const calculateSingles = (hits, doubles, triples, homeRuns) => {
  hits = ensureNumber(hits);
  doubles = ensureNumber(doubles);
  triples = ensureNumber(triples);
  homeRuns = ensureNumber(homeRuns);
  
  return Math.max(0, hits - doubles - triples - homeRuns);
};
  
/**
 * Calculate ERA (Earned Run Average)
 * @param {number} earnedRuns - Number of earned runs
 * @param {number} inningsPitched - Number of innings pitched
 * @returns {number} - Raw ERA (not formatted)
 */
export const calculateERA = (earnedRuns, inningsPitched) => {
  earnedRuns = ensureNumber(earnedRuns);
  inningsPitched = ensureNumber(inningsPitched);
  return inningsPitched > 0 ? (earnedRuns * 9) / inningsPitched : 0;
};

/**
 * Format a pitching statistic (e.g., 3.45 ERA)
 * @param {number} value - Value to format
 * @returns {string} - Formatted pitching statistic
 */
export const formatPitchingStat = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0.00';
  return value.toFixed(2);
};

/**
 * Calculate WHIP (Walks plus Hits per Inning Pitched)
 * @param {number} walks - Number of walks
 * @param {number} hits - Number of hits
 * @param {number} inningsPitched - Number of innings pitched
 * @returns {number} - Raw WHIP (not formatted)
 */
export const calculateWHIP = (walks, hits, inningsPitched) => {
  walks = ensureNumber(walks);
  hits = ensureNumber(hits);
  inningsPitched = ensureNumber(inningsPitched);
  return inningsPitched > 0 ? (walks + hits) / inningsPitched : 0;
};

/**
 * Calculate complete batting statistics
 * @param {object} stats - Raw batting statistics
 * @returns {object} - Complete batting statistics with calculated values
 */
export const calculateCompleteBattingStats = (stats) => {
  if (!stats) return {};
  
  // Ensure all values are numbers
  const processedStats = {};
  Object.keys(stats).forEach(key => {
    processedStats[key] = ensureNumber(stats[key]);
  });
  
  // Calculate derived stats
  const totalBases = calculateTotalBases(
    processedStats.hits,
    processedStats.doubles,
    processedStats.triples,
    processedStats.homeRuns
  );
  
  const avg = calculateBattingAverage(processedStats.hits, processedStats.atBats);
  const obp = calculateOBP(
    processedStats.hits,
    processedStats.walks,
    processedStats.hitByPitch,
    processedStats.atBats,
    processedStats.sacrificeFlies
  );
  const slg = calculateSLG(totalBases, processedStats.atBats);
  const ops = calculateOPS(obp, slg);
  
  return {
    ...processedStats,
    totalBases,
    average: avg,
    obp,
    slg,
    ops
  };
};
